import type { DeleteCheckResponse } from "@repo/shared/types";
import { useRef, useState } from "react";
import { log } from "@/lib/logger/browser-logger";

type UseDeleteCheckOptions = {
  /** The entity's `GET …/:id/delete-check` call. */
  check: (id: string) => Promise<DeleteCheckResponse>;
  /**
   * Nothing blocks the delete: open the confirmation. `notice` is what the
   * delete will also remove, for the confirmation text (null when nothing).
   */
  onAllowed: (id: string, notice: string | null) => void;
  /** The server would refuse the delete: show why instead of confirming. */
  onBlocked: (reason: string) => void;
};

/**
 * QL-168 — ask the server whether an entity can be deleted when Delete is
 * clicked, before the confirmation opens, so a refusal is shown straight away
 * instead of after the user has confirmed. The check is advisory: if it fails
 * the confirmation opens anyway, and the delete itself runs the same check.
 *
 * Only the latest click counts: an earlier check answering late is ignored, so
 * it can neither retarget an open confirmation nor raise an alert over it.
 *
 * `checkingId` is the id being checked, for a `loading` delete button.
 */
export function useDeleteCheck({ check, onAllowed, onBlocked }: UseDeleteCheckOptions) {
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const latestRun = useRef(0);

  const requestDelete = async (id: string) => {
    const run = ++latestRun.current;
    setCheckingId(id);
    try {
      const result = await check(id);
      if (run !== latestRun.current) return;
      if (result.canDelete) onAllowed(id, result.notice ?? null);
      else onBlocked(result.reason ?? "");
    } catch (err) {
      if (run !== latestRun.current) return;
      log.warn({ err }, "delete check failed, falling back to the confirmation");
      onAllowed(id, null);
    } finally {
      if (run === latestRun.current) setCheckingId(null);
    }
  };

  return { requestDelete, checkingId };
}
